import { Box, Collapse } from "@mui/material";
import React, { forwardRef, useMemo, useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import SecondaryButton from "../../../../../../../common/ui/SecondaryButton";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import HorizontalScrollableWrapper from "../../../../common/HorizontalScrollableWrapper";
import IntakeIcon from "../../../../../svg/IntakeIcon";
import ArrowDown from "../../../../../svg/ArrowDown";
import SecondaryTabMenuPicker from "../../../../common/SecondaryTabMenuPicker";
import { formatFullWeekdayShortDate } from "../../../../../../../common/utils/date";
import { formatMealTime } from "../../helpers/planDates";
import {
  calculateAvailableMealTypes,
  findMealByType,
  isMealLogged,
} from "../../helpers/planSelectors";
import { NutritionDailyType } from "../../model/nutritionPlanTypes";
import { useNutritionPlan } from "../../store/useNutritionPlan";
import DayMealSections from "../meals/DayMealSections";
import MealItemCard from "../meals/MealItemCard";

type Props = {
  day: NutritionDailyType;
  isToday: boolean;
};

/**
 * One day of the Week view, ported from the mobile `NutritionWeeklyDayComponent`:
 * title, kcal, an expand toggle and an icon-only Intake button. Collapsed,
 * the day's own meal-type tabs show one meal at a time as cards in a rail
 * (three across, more scroll sideways); switching to a later meal slides
 * the rail in from the right, to an earlier one from the left. Expanded, the tabs give way to the
 * full list of every meal (`DayMealSections`), with the chevron turning and
 * the body animating its height. Today starts expanded; the parent scrolls to it
 * through the forwarded ref.
 */
/** Expand / collapse duration, shared by the arrow and the body. */
const EXPAND_MS = 300;
/** How long the rail takes to slide in when the meal tab changes. */
const SLIDE_MS = 250;

const WeekDayRow = forwardRef<HTMLDivElement, Props>(({ day, isToday }, ref) => {
  const theme: Theme = useTheme();
  const { openDetails, openIntake } = useNutritionPlan();
  const [expanded, setExpanded] = useState(isToday);

  const mealTypes = useMemo(
    () => calculateAvailableMealTypes(day.meals),
    [day.meals]
  );
  const [selectedType, setSelectedType] = useState<number>(
    mealTypes[0]?.type ?? 1
  );
  // Which way the rail slides after a tab click: forward (a later meal)
  // enters from the right, back from the left. Null when nothing is sliding,
  // so the first render, a refresh and re-expanding the row stay still; the
  // rail is keyed by the meal so a switch remounts it and the slide plays,
  // and the direction clears again once the slide has ended.
  const [slide, setSlide] = useState<"forward" | "back" | null>(null);

  const selectMeal = (index: number) => {
    const next = tabs[index].key;
    const current = tabs.findIndex((tab) => tab.key === selectedType);
    setSlide(index >= current ? "forward" : "back");
    setSelectedType(next);
  };

  const tabs = mealTypes.map((type) => {
    const meal = findMealByType(day, type.type);
    const time = formatMealTime(meal?.time_of_consumption);
    return {
      label: time ? `${type.label} · ${time}` : type.label,
      key: type.type,
      isLogged: isMealLogged(meal),
    };
  });
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === selectedType)
  );
  const items = findMealByType(day, selectedType)?.items ?? [];

  return (
    <Box ref={ref} sx={styles.row(theme, isToday)}>
      <Box sx={styles.header}>
        <Box sx={styles.titleGroup}>
          <Box sx={styles.title(theme)}>
            {formatFullWeekdayShortDate(day.date)}
          </Box>
          {isToday && <Box sx={styles.todayPill(theme)}>Today</Box>}
          <Box sx={styles.kcal(theme)}>
            {Math.round(day.declaration?.cals ?? 0)} kcal
          </Box>
        </Box>
        <Box sx={styles.actions}>
          {/* Icon only, as the mobile weekly rows do: a labelled button on
              every row read as a column of "Intake" repeated. The name stays
              for assistive tech and the hover tooltip. */}
          <SecondaryButton
            label=""
            aria-label="Intake"
            title="Intake"
            icon={<IntakeIcon size={16} fill={theme.colors_ds.PRIMARY_ICON_MAIN} />}
            onClick={() => openIntake(day.date, 0)}
          />
          <SecondaryButton
            label=""
            aria-label={expanded ? "Collapse day" : "Expand day"}
            aria-expanded={expanded}
            title={expanded ? "Collapse day" : "Expand day"}
            icon={
              <Box sx={styles.expandIcon(expanded)}>
                <ArrowDown width={16} height={16} />
              </Box>
            }
            onClick={() => {
              // A rail re-mounted by collapsing must not replay the last slide.
              setSlide(null);
              setExpanded((value) => !value);
            }}
          />
        </Box>
      </Box>
      {/* The two bodies cross-fade in height: the full list grows as the
          tabbed one shrinks, and vice versa. */}
      <Collapse in={expanded} timeout={EXPAND_MS} unmountOnExit>
        <DayMealSections
          day={day}
          onOpenItem={(mealType, item) => openDetails(day.date, mealType, item)}
        />
      </Collapse>
      <Collapse in={!expanded} timeout={EXPAND_MS} unmountOnExit>
        <Box sx={styles.tabbedBody}>
      {tabs.length > 0 && (
        <HorizontalScrollableWrapper>
          <SecondaryTabMenuPicker
            tabs={tabs}
            selectedTab={selectedIndex}
            setSelectedTab={selectMeal}
            isFullWidth={false}
            tabStyle={{ whiteSpace: "nowrap" }}
          />
        </HorizontalScrollableWrapper>
      )}
      <Box
        key={selectedType}
        sx={styles.slideIn(slide)}
        onAnimationEnd={() => setSlide(null)}
      >
        {items.length === 0 ? (
          <Box sx={styles.empty(theme)}>No items for this meal.</Box>
        ) : (
          <HorizontalScrollableWrapper gap="16px" stretch>
            {items.map((rootItem) => (
              <Box key={rootItem.id} sx={styles.cardSlot}>
                <MealItemCard
                  rootItem={rootItem}
                  onOpen={(item) => openDetails(day.date, selectedType, item)}
                />
              </Box>
            ))}
          </HorizontalScrollableWrapper>
        )}
      </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

WeekDayRow.displayName = "WeekDayRow";

export default WeekDayRow;

const styles = {
  row: (theme: Theme, isToday: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${
      isToday ? theme.colorTokens.P[60] : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  // The chevron turns to point up while the day is open.
  expandIcon: (expanded: boolean) => ({
    display: "flex",
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: `transform ${EXPAND_MS}ms ease`,
  }),
  // The collapsed body keeps the row's own vertical rhythm inside the Collapse.
  tabbedBody: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  todayPill: (theme: Theme) => ({
    padding: "2px 8px",
    borderRadius: "12px",
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  kcal: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
  }),
  // After a tab click the entering meal's rail slides in from the side it
  // comes from and fades up; with no direction set it just appears.
  slideIn: (direction: "forward" | "back" | null) => ({
    minWidth: 0,
    animation: direction
      ? `${
          direction === "forward" ? "mealSlideForward" : "mealSlideBack"
        } ${SLIDE_MS}ms ease-out`
      : "none",
    "@keyframes mealSlideForward": {
      from: { opacity: 0, transform: "translateX(32px)" },
      to: { opacity: 1, transform: "translateX(0)" },
    },
    "@keyframes mealSlideBack": {
      from: { opacity: 0, transform: "translateX(-32px)" },
      to: { opacity: 1, transform: "translateX(0)" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  }),
  // Three cards fill the row (two 16px gaps between them); a fourth and
  // beyond scroll sideways in the rail. The rail stretches the slots to the
  // tallest one and the slot is a grid, so every card gets the same height.
  cardSlot: {
    width: "calc((100% - 32px) / 3)",
    display: "grid",
  },
  empty: (theme: Theme) => ({
    padding: "16px 0",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
};
